import { CrmTaskStatus } from '@plato/data/prisma';
import { SearchEntity } from '@plato/search/search-types';
import { RelativeDateText } from '@plato/ui-legacy/components/common/relative-date-text';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import Text from '@plato/ui-legacy/components/ui/text';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import { ActivityFeedModelType } from '../../../server/types/customer-activity-feed-types.ts';
import { LogInteractionDialog } from '../../notes/components/log-interaction-dialog.tsx';
import { NoDocumentState } from '../../shared/components/no-document-state/no-document-state.tsx';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';
import { useInvalidateFeed } from '../hooks/use-activity-actions.ts';
import { useActivityFilters } from '../hooks/use-activity-filters.ts';

function TaskEmptyState({ customer }: { customer: CustomerDTO }): ReactNode {
    const { t } = useTranslation('activity-feed');
    const showCreateTaskDialog = useShowCreateTaskDialog();

    const { appliedFilters } = useActivityFilters();
    const taskStatuses = appliedFilters.taskStatuses || [];

    const handleCreateTaskClick = (): void => {
        showCreateTaskDialog({
            links: [
                {
                    modelType: SearchEntity.Customer,
                    modelId: customer.customerId,
                    title: customer.customerName ?? null,
                },
            ],
            source: 'customer_activity',
        });
    };

    const hasCompleteStatus = taskStatuses.includes(CrmTaskStatus.COMPLETED);
    const hasOpenStatus = taskStatuses.includes(CrmTaskStatus.OPEN);

    if (taskStatuses.length === 0 || (hasCompleteStatus && hasOpenStatus)) {
        return (
            <NoDocumentState>
                <NoDocumentState.Title>{t('customer_detail__no_tasks')}</NoDocumentState.Title>
                <NoDocumentState.Description>
                    {t('customer_detail__no_tasks_description')}
                </NoDocumentState.Description>
                <div>
                    <Button variant="outline" onClick={handleCreateTaskClick}>
                        {t('customer_detail__add_task')}
                    </Button>
                </div>
            </NoDocumentState>
        );
    }

    if (hasOpenStatus) {
        return (
            <div className="flex flex-col gap-3">
                <Text weight="medium">
                    {t('tasks_open_tasks', {
                        ns: 'tasks',
                    })}
                </Text>
                <NoDocumentState>
                    <NoDocumentState.Title>{t('customer_detail__no_open_tasks')}</NoDocumentState.Title>
                    <NoDocumentState.Description>
                        {t('customer_detail__no_open_tasks_description')}
                    </NoDocumentState.Description>
                    <div>
                        <Button variant="outline" onClick={handleCreateTaskClick}>
                            {t('customer_detail__add_task')}
                        </Button>
                    </div>
                </NoDocumentState>
            </div>
        );
    }

    if (hasCompleteStatus) {
        return (
            <div className="flex flex-col gap-3">
                <Text weight="medium">
                    <RelativeDateText date={new Date()} />
                </Text>
                <NoDocumentState>
                    <NoDocumentState.Title>{t('customer_detail__no_completed_tasks')}</NoDocumentState.Title>
                    <NoDocumentState.Description>
                        {t('customer_detail__no_completed_tasks_description')}
                    </NoDocumentState.Description>
                </NoDocumentState>
            </div>
        );
    }

    return null;
}

function NoteEmptyState({ customer }: { customer: CustomerDTO }): ReactNode {
    const { t } = useTranslation('activity-feed');
    const invalidateFeed = useInvalidateFeed();

    const handleAddNote = (): void => {
        void DialogStateManager.show(LogInteractionDialog, {
            customer,
            onSuccess() {
                invalidateFeed();
            },
            analyticsSource: 'customer_detail',
        });
    };

    return (
        <NoDocumentState>
            <NoDocumentState.Title>{t('customer_detail__no_notes')}</NoDocumentState.Title>
            <NoDocumentState.Description>
                {t('customer_detail__no_notes_description')}
            </NoDocumentState.Description>
            <div>
                <Button variant="outline" onClick={handleAddNote}>
                    {t('customer_detail__add_note')}
                </Button>
            </div>
        </NoDocumentState>
    );
}

function QuoteOrderEmptyState(): ReactNode {
    const { t } = useTranslation('activity-feed');

    return (
        <NoDocumentState>
            <NoDocumentState.Title>
                {t('nothing_found', {
                    ns: 'common',
                })}
            </NoDocumentState.Title>
            <NoDocumentState.Description>
                {t('nothing_found', {
                    ns: 'common',
                })}
            </NoDocumentState.Description>
        </NoDocumentState>
    );
}

function DefaultEmptyState(): ReactNode {
    const { t } = useTranslation('activity-feed');

    return (
        <NoDocumentState>
            <NoDocumentState.Title>
                {t('nothing_found', {
                    ns: 'common',
                })}
            </NoDocumentState.Title>
            <NoDocumentState.Description>
                {t('nothing_found', {
                    ns: 'common',
                })}
            </NoDocumentState.Description>
        </NoDocumentState>
    );
}

export function ActivityEmptyState({ customer }: { customer: CustomerDTO }): ReactNode {
    const {
        appliedFilters: { modelType },
    } = useActivityFilters();

    switch (modelType) {
        case ActivityFeedModelType.NOTE:
            return <NoteEmptyState customer={customer} />;
        case ActivityFeedModelType.TASK:
            return <TaskEmptyState customer={customer} />;
        case ActivityFeedModelType.QUOTE:
        case ActivityFeedModelType.ORDER:
            return <QuoteOrderEmptyState />;
        default:
            return <DefaultEmptyState />;
    }
}
